import * as RadixToggle from '@radix-ui/react-toggle';
import { cva, cx } from 'class-variance-authority';
import { forwardRef, useId, useRef } from 'react';
import { useTimeout } from 'usehooks-ts';

export type ToggleProps = {
  /** Accessible name for a switch that has no visible `label`. */
  'aria-label'?: string;
  autoFocus?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: string | React.ReactNode;
  hint?: string;
  labelPlacement?: 'left' | 'right';
  onChange?: (checked: boolean) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  size?: 'default' | 'compact';
  className?: string;
  fullWidth?: boolean;
};

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      'aria-label': ariaLabel,
      autoFocus = false,
      label,
      hint,
      labelPlacement = 'right',
      checked,
      defaultChecked,
      disabled = false,
      onChange,
      onBlur,
      size = 'default',
      className,
      fullWidth = false,
    }: ToggleProps,
    ref,
  ) => {
    const id = useId();
    const toggleRef = useRef<HTMLButtonElement>(null);

    useTimeout(() => {
      if (autoFocus) {
        toggleRef.current?.focus();
      }
    }, 100);

    return (
      <RadixToggle.Root
        id={id}
        role="switch"
        aria-label={ariaLabel}
        disabled={disabled}
        defaultPressed={defaultChecked}
        pressed={checked}
        onPressedChange={onChange}
        onBlur={onBlur}
        className={toggleVariants({
          labelPlacement,
          hasHint: !!hint,
          className,
          disabled,
          fullWidth,
        })}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        ref={ref}
      >
        {(label || hint) && (
          <div className="flex flex-col flex-1 min-w-0">
            {label && (
              <label htmlFor={id} className={cx(labelVariants({ size }))}>
                {label}
              </label>
            )}
            {hint && <div className={cx(hintVariants({ size }))}>{hint}</div>}
          </div>
        )}

        <span
          className={switchVariants({
            size,
            disabled,
            pressed: checked ?? false,
          })}
        >
          <svg
            className={circleVariants({
              size,
              disabled,
              pressed: checked ?? false,
            })}
            viewBox="0 0 16 16"
          >
            <circle cx="8" cy="8" r="8" fill="currentColor" />
          </svg>
        </span>
      </RadixToggle.Root>
    );
  },
);

const toggleVariants = cva('gap-3 group flex', {
  variants: {
    fullWidth: {
      true: 'w-full',
      false: 'w-fit',
    },
    labelPlacement: {
      right: 'flex-row-reverse',
      left: 'flex-row',
    },
    // A label + hint stack is taller than the switch, so it top-aligns; a bare label centers against
    // it. Both must live here: Tailwind emits `items-center` before `items-start`, so an
    // `items-start` in the base classes would win over this variant rather than be overridden.
    hasHint: {
      false: 'items-center',
      true: 'items-start',
    },
    disabled: {
      true: '',
      false: 'cursor-pointer',
    },
    size: {
      compact: '',
      default: '',
    },
  },
});

const switchVariants = cva(
  [
    'relative rounded-full border-2 border-solid transition-all duration-200 ease-in-out overflow-hidden',
    'group-focus-visible:ring-2 ring-offset-2',
  ],
  {
    variants: {
      size: {
        compact: 'w-8 h-5',
        default: 'w-9 h-6',
      },
      disabled: {
        true: 'pointer-events-none opacity-40',
        false: '',
      },
      pressed: {
        true: 'bg-accent border-transparent hover:opacity-90',
        false: 'border-primary bg-secondary hover:bg-hover-control',
      },
    },
    defaultVariants: {
      size: 'default',
      pressed: false,
    },
  },
);

const circleVariants = cva(['absolute transition-transform duration-200 left-0.5 top-1/2 -translate-y-1/2'], {
  variants: {
    size: {
      compact: 'w-3 h-3',
      default: 'w-4 h-4',
    },
    disabled: {
      true: 'opacity-100',
      false: '',
    },
    pressed: {
      true: 'translate-x-[13px] text-quinary',
      false: 'text-tertiary',
    },
  },
});

const labelVariants = cva('text-left text-primary', {
  variants: {
    size: {
      compact: 'text-body-2',
      default: 'text-body-2',
    },
  },
});

const hintVariants = cva('text-left text-tertiary break-words', {
  variants: {
    size: {
      compact: 'text-body-2',
      default: 'text-body-2',
    },
  },
});

export default Toggle;
